import { SECTIONS } from '../lib/sectionTheme';

const C = SECTIONS.consent;
const blank = '\u00A0';
const BOX = '\u2610\uFE0E';
const TICK = '\u2611\uFE0E';
const box = (on) => <span style={{ fontFamily: "'DejaVu Sans','Segoe UI Symbol',sans-serif", color: on ? '#52c3c5' : '#0d2644' }}>{on ? TICK : BOX}</span>;
const BT_TEXT = {
  none: 'I do not take any blood thinning medication.',
  ceased: 'I take blood thinning medication and have stopped it as instructed',
  still_taking: 'I am still taking my blood thinning medication.',
};

// v2.1 two-page consent — mirrors the SP "Image-Guided Procedure Consent & Patient Declaration".
export function ConsentDocV2({ template: t, practice = {}, patient = {}, biopsySite, ticks = {}, decl = {},
  signature, radName, signedAt, countersignedAt, countersignedBy }) {
  const fmt = (d) => (d ? new Date(d).toLocaleString('en-AU') : '');
  const witness = radName || countersignedBy?.name;

  return (
    <div className="consent-doc" data-testid="consent-doc">
      <div className="brand-rule" />
      <div className="legal-header">
        <div className="brand-left">
          <img src="/brand/i3-mark.png" alt="" aria-hidden />
          <div>
            <p className="small" style={{ color: '#0d2644', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              {practice.practice_name || 'i3 Radiology'}
            </p>
            <h1 className="legal-title" data-testid="consent-doc-title">{t.title}</h1>
            <p className="small" style={{ marginTop: 4, fontStyle: 'italic' }}>Image-Guided Procedure Consent &amp; Patient Declaration</p>
          </div>
        </div>
        <div className="legal-meta">
          <div>Version {t.version}</div>
          <div>{signedAt ? fmt(signedAt) : 'Draft — live preview'}</div>
        </div>
      </div>

      <h2>1 · The procedure</h2>
      <p><strong>Your doctor has requested an image-guided procedure: {t.procedure_type}.</strong></p>
      <p style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{t.procedure_description}</p>

      <h2>2 · Purpose</h2>
      <ul>{(t.purpose || []).map((p, i) => <li key={i}>{p}</li>)}</ul>

      {t.site_picker && <SiteRisks picker={t.site_picker} selected={biopsySite} />}

      <h3 style={H3}>Pre-procedure safety checklist</h3>
      <ul>
        {(t.safety_checklist || []).map((p, i) => <li key={i}>{p}</li>)}
        {t.safety_checklist_footnote && <li>{t.safety_checklist_footnote}</li>}
      </ul>

      <p style={{ marginTop: 8 }}><strong>Blood thinners:</strong> {t.blood_thinners_note}</p>
      {t.diabetes_note && <p style={{ marginTop: 6 }}><strong>Diabetes:</strong> {t.diabetes_note}</p>}
      {t.biopsy_post && <p style={{ marginTop: 6 }}><strong>After the biopsy:</strong> {t.biopsy_post}</p>}
      <p style={{ marginTop: 6 }} data-testid="consent-doc-post-care"><strong>Post-procedure care:</strong> {t.post_care_note}</p>

      <h2>3 · Patient details</h2>
      <div data-testid="consent-doc-patient">
        <Line label="Full name" value={patient.name} />
        <Line label="Phone" value={patient.mobile || patient.phone} />
        <Line label="Date of birth" value={patient.dob} />
      </div>

      <h2>4 · Health declarations</h2>
      <div data-testid="consent-doc-health">
        <p style={{ marginBottom: 8 }}>
          <strong>Blood thinning medication: </strong>
          {decl.btStatus ? (
            <span data-testid="consent-doc-bt">
              {BT_TEXT[decl.btStatus]}{decl.btStatus === 'ceased' ? `${decl.btCeased ? ` on ${decl.btCeased}` : ''}.` : ''}
            </span>
          ) : (
            <span>{box(false)} {BT_TEXT.none} &nbsp; {box(false)} Stopped on ________ &nbsp; {box(false)} Still taking</span>
          )}
        </p>
        <Line label="Allergies" value={decl.allergies} />
        <p style={{ margin: '6px 0 10px' }} data-testid="consent-doc-diabetic">
          <strong>Are you a diabetic?</strong> &nbsp;{box(decl.diabetic === true)} Yes &nbsp; {box(decl.diabetic === false)} No
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Area label="Current medications" value={decl.medications} testid="consent-doc-meds" />
          <Area label="Previous surgeries" value={decl.surgeries} testid="consent-doc-surgeries" />
        </div>
        {t.covid_questions && (
          <div data-testid="consent-doc-covid" style={{ marginTop: 8 }}>
            <YN label="COVID vaccine (last 2 weeks)" value={decl.covid2w} />
            <YN label="COVID vaccine (next week)" value={decl.covidNext} />
            <p style={{ marginTop: 4, fontSize: 10.5, fontStyle: 'italic', color: '#374151' }}>{t.covid_note}</p>
          </div>
        )}
      </div>

      <h2>5 · Consent declaration</h2>
      <p style={{ marginBottom: 8 }}>I confirm that:</p>
      <div className="declaration" data-testid="consent-doc-declaration">
        <ul>
          {(t.consent_statement || []).map(c => (
            <li key={c.id} className={ticks?.[c.id] ? 'checked' : ''}>{c.text}</li>
          ))}
        </ul>
      </div>

      <h3 style={H3}>Time-out (immediately before the procedure)</h3>
      <p>{t.timeout_note}</p>

      <div className="sig-grid">
        <Sig label="Patient signature" value={signature} date={signedAt} />
        <Sig label="Radiologist / clinician (witness)" value={witness} date={countersignedAt} />
      </div>

      <div className="footer">
        <span>{practice.practice_name || 'i3 Radiology'} · Image-Guided Procedure Consent</span>
        <span>{t.title} · v{t.version}</span>
      </div>
    </div>
  );
}

const H3 = { fontSize: 11.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#0d2644', marginTop: 14, marginBottom: 6 };

function YN({ label, value }) {
  return (
    <p style={{ margin: '4px 0' }}>
      <strong>{label}:</strong> &nbsp;{box(value === true)} Yes &nbsp; {box(value === false)} No
    </p>
  );
}

function Line({ label, value }) {
  return <div className="pt-row"><span className="pt-label">{label}</span><span className="pt-field">{value || blank}</span></div>;
}

function Area({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>{label}</div>
      <div style={{ border: '1px dashed #9ca3af', borderRadius: 4, minHeight: 78, padding: '4px 6px', fontSize: 12.5, whiteSpace: 'pre-wrap' }}>
        {value || blank}
      </div>
    </div>
  );
}

function Sig({ label, value, date }) {
  return (
    <div>
      <div className="sig-box">{value || blank}</div>
      <div className="sig-label">{label}</div>
      <div className="small" style={{ marginTop: 2 }}>{date ? new Date(date).toLocaleString('en-AU') : 'Date: __________________'}</div>
    </div>
  );
}

function SiteRisks({ picker, selected }) {
  return (
    <>
      <h3 style={H3}>{picker.title || 'Site-specific risks'}</h3>
      <ul data-testid="consent-doc-site-risks">
        {(picker.options || []).map(o => {
          const on = selected?.key === o.key;
          return (
            <li key={o.key} style={on ? { fontWeight: 600, background: `${C.color}14`, borderRadius: 3 } : undefined}>
              <strong>{on ? '☑ ' : ''}{o.label}.</strong> {o.risk_text}
            </li>
          );
        })}
      </ul>
    </>
  );
}

const inputCls = 'w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2';
const lbl = 'text-[10px] font-bold uppercase tracking-wider text-muted-foreground';

function YesNo({ label, value, onChange, testid }) {
  return (
    <div>
      <span className={lbl}>{label}</span>
      <div className="flex gap-2 mt-1">
        {[['Yes', true], ['No', false]].map(([t, v]) => (
          <button key={t} type="button" onClick={() => onChange(value === v ? null : v)} data-testid={`${testid}-${t.toLowerCase()}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${value === v ? 'text-white' : 'text-foreground'}`}
            style={value === v ? { backgroundColor: C.color, borderColor: C.color } : undefined}>{t}</button>
        ))}
      </div>
    </div>
  );
}

// Left-panel inputs for section 4 (Health declarations). All optional — blank prints as handwriting space.
export function HealthDeclarationsCard({ template: t, decl, setDecl }) {
  const set = (k) => (v) => setDecl({ ...decl, [k]: v });
  return (
    <div className="bg-card border border-border rounded-2xl p-4 space-y-3" data-testid="c-health-card">
      <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.color }}>Health declarations</p>
      <div>
        <span className={lbl}>Blood thinning medication</span>
        <div className="space-y-1.5 mt-1">
          {[['none', 'I do not take any'], ['ceased', 'Stopped as instructed'], ['still_taking', 'Still taking']].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="radio" checked={decl.btStatus === k} onChange={() => set('btStatus')(k)} data-testid={`c-bt-${k}`} /> {l}
            </label>
          ))}
        </div>
        {decl.btStatus === 'ceased' && (
          <input type="text" value={decl.btCeased || ''} onChange={e => set('btCeased')(e.target.value)}
            placeholder="Date stopped (DD/MM/YYYY)" className={inputCls} data-testid="c-bt-ceased-date" />
        )}
      </div>
      <label className="block"><span className={lbl}>Allergies</span>
        <input type="text" value={decl.allergies || ''} onChange={e => set('allergies')(e.target.value)} className={inputCls} data-testid="c-allergies" />
      </label>
      <YesNo label="Are you a diabetic?" value={decl.diabetic} onChange={set('diabetic')} testid="c-diabetic" />
      <label className="block"><span className={lbl}>Current medications</span>
        <textarea rows={3} value={decl.medications || ''} onChange={e => set('medications')(e.target.value)} className={inputCls} data-testid="c-medications" />
      </label>
      <label className="block"><span className={lbl}>Previous surgeries</span>
        <textarea rows={3} value={decl.surgeries || ''} onChange={e => set('surgeries')(e.target.value)} className={inputCls} data-testid="c-surgeries" />
      </label>
      {t.covid_questions && (
        <div className="grid grid-cols-2 gap-2">
          <YesNo label="COVID vaccine (last 2 weeks)" value={decl.covid2w} onChange={set('covid2w')} testid="c-covid2w" />
          <YesNo label="COVID vaccine (next week)" value={decl.covidNext} onChange={set('covidNext')} testid="c-covidnext" />
        </div>
      )}
    </div>
  );
}
